import { useEffect, useState, Suspense, lazy } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { routeConstants, routePaths } from './routes/routeConstants'
import { setAppPermissions } from './pages/dashboard/dasboardSlice'
import { dark, light } from './resources/theme/mode'
import {
	Box,
	CssBaseline,
	LinearProgress,
	ThemeProvider,
	Typography,
	createTheme,
} from '@mui/material'
import { englishTypography } from './resources/theme/typography'
import { getUserFromLocalStorage, addUserToLocalStorage } from './utils/utils'
import { fetchMyPermissions } from './features/rolePermissions/services/rolePermissionApi'
import { HelpCenterProvider } from './features/helpCenter/context/HelpCenterContext'
import { WellbeingProvider } from './features/wellbeing'

import { resetLoginSlice } from './pages/login/loginSlice'
import { clearCounsellorSlice } from './pages/counsellors/counsellorSlice'
import { resetSchoolSlice } from './pages/academic/school/schoolSlice'
import { resetStudentSlice } from './pages/academic/students/studentsSlice'
import { resetIndividualCaseSlice } from './pages/initiations/individualCase/individualCaseSlice'
import { resetBaselineSlice } from './pages/initiations/baseline/baselineSlice'
import { resetObservationSlice } from './pages/initiations/observation/observationSlice'
import { resetClassroomsSlice } from './pages/academic/classrooms/classroomsSlice'
import { clearStudentWellBeingSlice } from './pages/assessment/studentWellBeing/StudentWellBeingSlice'
import { clearSendchecklistsliceBeingSlice } from './pages/initiations/sendCheckList/sendChecklistslice'
import { clearStudentIEPSlice } from './pages/initiations/IEP/iEPSlice'

const Login = lazy(() => import('./pages/login/Login'))
const AccountRecovery = lazy(
	() => import('./pages/accountRecovery/AccountRecovery'),
)
const CreateNewPassword = lazy(
	() => import('./pages/createNewPassword/CreateNewPassword'),
)
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'))
const ProtectedRoute = lazy(() => import('./ProtectedRoute'))
const FeatureRoute = lazy(() => import('./components/FeatureRoute'))
const DashboardContent = lazy(
	() => import('./pages/dashboardContent/DashboardContent'),
)
const PageNotFound = lazy(() => import('./components/PageNotFound'))
const ApplicationRoot = lazy(() => import('./components/ApplicationRoot'))
const Authentication = lazy(() => import('./components/Authentication'))
const Toast = lazy(() => import('./components/Toast'))
const MakerMark = lazy(() => import('./components/MakerMark'))
const Schools = lazy(() => import('./pages/academic/school/Schools'))
const Counsellors = lazy(() => import('./pages/counsellors/Counsellors'))
const DeactivatedUsersPage = lazy(
	() => import('./features/deactivatedUsers/pages/DeactivatedUsersPage'),
)
const ActivateAccount = lazy(
	() => import('./pages/activateAccount/ActivateAccount'),
)
const Students = lazy(() => import('./pages/academic/students/Students'))
const ClassRooms = lazy(() => import('./pages/academic/classrooms/Classrooms'))
const Teachers = lazy(() => import('./pages/academic/teachers/Teachers'))
const IndividualCase = lazy(
	() => import('./pages/initiations/individualCase/IndividualCase'),
)

const Observation = lazy(
	() => import('./pages/initiations/observation/Observation'),
)
const ObservationV2List = lazy(
	() => import('./features/observationV2/pages/ObservationV2List'),
)
const ObservationV2AddEdit = lazy(
	() => import('./features/observationV2/pages/ObservationV2AddEdit'),
)
const Baseline = lazy(() => import('./pages/initiations/baseline/Baseline'))
const StudentBaseline = lazy(
	() => import('./pages/initiations/baseline/StudentBaselineReport'),
)
const SelfAssessmentBaseline = lazy(
	() => import('./pages/initiations/baseline/SelfAssessmentBaseline'),
)
const SelfAssessmentDashboard = lazy(
	() => import('./pages/initiations/selfAssessment/SelfAssessmentDashboard'),
)
const SchoolProgressDashboard = lazy(
	() => import('./features/schoolProgress/pages/SchoolProgressDashboard'),
)
const InclusionReportPage = lazy(
	() => import('./features/inclusionReport/pages/InclusionReportPage'),
)

const Sel = lazy(() => import('./pages/initiations/SELCurriculumTracker/SEL'))
const SelTemplateAdmin = lazy(() => import('./pages/initiations/SELCurriculumTracker/SelTemplateAdmin'))
const StudentCope = lazy(
	() => import('./pages/assessment/StudentCope/StudentCope'),
)
const TeacherIRI = lazy(
	() => import('./pages/assessment/teacherIRI/TeacherIRI'),
)
const TeacherIRITeachersList = lazy(
	() => import('./pages/assessment/teacherIRI/TeacherIRITeachersList'),
)
const TeacherIRIAssessment = lazy(
	() => import('./pages/assessment/teacherIRI/TeacherIRIAssessment'),
)

const SafeSpaces = lazy(
	() => import('./pages/newAssesment/safeSpaces/SafeSpaces'),
)
const SELCompetency = lazy(
	() => import('./pages/newAssesment/SELCompetency/SELAssessment'),
)
const BipListPage = lazy(() => import('./features/bip/pages/BipListPage'))
const BipWizardPage = lazy(() => import('./features/bip/pages/BipWizardPage'))
const Pulse = lazy(() => import('./pages/newAssesment/pulse/Pulse'))
const HeartEducators = lazy(
	() => import('./pages/newAssesment/heartEducators/Heart'),
)
const TeacherProfilingAssessment = lazy(
	() =>
		import('./pages/assessment/TeacherProfiling/TeacherProfilingAssessment'),
)
const StudentWellBeing = lazy(
	() => import('./pages/assessment/studentWellBeing/StudentWellBeing'),
)
const GandTTemplateList = lazy(
	() => import('./pages/gandt/templates/GandTTemplateList'),
)
const GandTTemplateForm = lazy(
	() => import('./pages/gandt/templates/GandTTemplateForm'),
)
const GandTTemplateView = lazy(
	() => import('./pages/gandt/templates/GandTTemplateView'),
)
const GandTAssignmentList = lazy(
	() => import('./pages/gandt/assignments/GandTAssignmentList'),
)
const SendChecklist = lazy(
	() => import('./pages/initiations/sendCheckList/SendCheckList'),
)
const StudentIEP = lazy(() => import('./pages/initiations/IEP/StudentIEP'))

const StudentAIIEP = lazy(
	() => import('./pages/initiations/IEP/AIIEP/StudentAIIEP'),
)
const AIIEPDashboard = lazy(
	() => import('./pages/initiations/IEP/AIIEP/AIIEPDashboard'),
)
const IEPMasterDashboard = lazy(
	() => import('./pages/initiations/IEP/IEPMasterDashboard'),
)
const Sessions = lazy(() => import('./pages/virtualCounselling/Sessions'))
const VirtualSchoolPage = lazy(
	() => import('./pages/virtualCounselling/VirtualSchoolPage'),
)
const SessionHistory = lazy(
	() => import('./pages/virtualCounselling/SessionHistory'),
)
const VirtualCounsellorPage = lazy(
	() => import('./pages/virtualCounselling/VirtualCounsellorPage'),
)
const SchoolPortalLogin = lazy(
	() => import('./pages/virtualCounselling/SchoolPortalLogin'),
)
const SchoolPortalDashboard = lazy(
	() => import('./pages/virtualCounselling/SchoolPortalDashboard'),
)
const SchoolProtectedRoute = lazy(
	() => import('./features/meetings/SchoolProtectedRoute'),
)
const ManageSchoolCredentials = lazy(
	() => import('./pages/virtualCounselling/ManageSchoolCredentials'),
)

const ScheduleMeeting = lazy(
	() => import('./pages/virtualCounselling/ScheduleMeeting'),
)

const SafeSpaceHub = lazy(() => import('./pages/safeSpaceSurvey/SafeSpaceHub'))
const SafeSpaceCreateSession = lazy(
	() => import('./pages/safeSpaceSurvey/SafeSpaceCreateSession'),
)
const SafeSpaceSessions = lazy(
	() => import('./pages/safeSpaceSurvey/SafeSpaceSessions'),
)
const SafeSpaceHistory = lazy(
	() => import('./pages/safeSpaceSurvey/SafeSpaceHistory'),
)
const SafeSpaceSessionDetail = lazy(
	() => import('./pages/safeSpaceSurvey/SafeSpaceSessionDetail'),
)
const SafeSpaceReportPage = lazy(
	() => import('./pages/safeSpaceSurvey/SafeSpaceReportPage'),
)
const SafeSpaceDashboard = lazy(
	() => import('./pages/safeSpaceSurvey/SafeSpaceDashboard'),
)

// const Reports = lazy(() => import('./pages/virtualCounselling/Reports'))

const ResourceDashboard = lazy(
	() => import('./features/resources/pages/ResourceDashboard'),
)
const ResourceListPage = lazy(
	() => import('./features/resources/pages/ResourceList'),
)
const UploadResourcePage = lazy(
	() => import('./features/resources/pages/UploadResourcePage'),
)
const ResourcePreview = lazy(
	() => import('./features/resources/components/Preview'),
)
const SuperAdminRoute = lazy(() => import('./components/SuperAdminRoute'))
const RolePermissionManagement = lazy(
	() => import('./features/rolePermissions/pages/RolePermissionManagement'),
)
const SystemConfiguration = lazy(
	() => import('./features/systemConfig/pages/SystemConfiguration'),
)
const SchoolConfiguration = lazy(
	() => import('./features/schoolConfiguration/pages/SchoolConfiguration'),
)
const BackupLogs = lazy(
	() => import('./features/systemConfig/pages/BackupLogs'),
)
const ActivityAuditPage = lazy(
	() => import('./features/activityAudit/pages/ActivityAuditPage'),
)
const UserActivityReportPage = lazy(
	() => import('./features/activityAudit/pages/UserActivityReportPage'),
)
const StudentSupportStaffRoute = lazy(
	() => import('./components/StudentSupportStaffRoute'),
)
const StudentSupportOverview = lazy(
	() => import('./features/studentSupportAdmin/pages/StudentSupportOverview'),
)
const StudentSupportSessions = lazy(
	() => import('./features/studentSupportAdmin/pages/StudentSupportSessions'),
)
const StudentSupportSessionDetail = lazy(
	() => import('./features/studentSupportAdmin/pages/StudentSupportSessionDetail'),
)
const StudentSupportRequests = lazy(
	() => import('./features/studentSupportAdmin/pages/StudentSupportRequests'),
)
const CounsellorRegistrationList = lazy(
	() =>
		import(
			'./features/counsellorRegistration/pages/CounsellorRegistrationList'
		),
)
const WebsiteInquiriesPage = lazy(
	() => import('./features/websiteInquiries/pages/WebsiteInquiriesPage'),
)
const CounsellorRegistrationDetail = lazy(
	() =>
		import(
			'./features/counsellorRegistration/pages/CounsellorRegistrationDetail'
		),
)
const PublicCounsellorRegistrationForm = lazy(
	() =>
		import(
			'./features/counsellorRegistration/pages/PublicCounsellorRegistrationForm'
		),
)
const CounsellorFeedbackAdminPage = lazy(
	() =>
		import(
			'./features/counsellingFeedbackAdmin/pages/CounsellorFeedbackAdminPage'
		),
)
const CounsellorFeedbackWidget = lazy(
	() =>
		import(
			'./features/counsellingFeedback/components/CounsellorFeedbackWidget'
		),
)
const HelpCenterHost = lazy(
	() => import('./features/helpCenter/components/HelpCenterHost'),
)
const SosHost = lazy(() => import('./features/helpCenterSos/components/SosHost'))
const SosAdminPage = lazy(
	() => import('./features/helpCenterSos/pages/SosAdminPage'),
)

// const ChatSupport = lazy(() => import('./pages/virtualCounselling/ChatSupport'))

const GandT = lazy(() => import('./pages/initiations/gandt/ComingSoon'))
const ItapReportPage = lazy(() => import('./pages/initiations/gandt/ItapReportPage'))

const SchoolIRIAnalytics = lazy(
	() => import('./pages/assessment/teacherIRI/SchoolIRIAnalytics'),
)
const SpecificSchoolIRIDetails = lazy(
	() => import('./pages/assessment/teacherIRI/SpecificSchoolIRIDetails'),
)
const TeacherProfilingSchoolList = lazy(
	() => import('./pages/assessment/TeacherProfiling/TeacherProfilingSchoolList'),
)
const TeacherProfilingTeachersList = lazy(
	() => import('./pages/assessment/TeacherProfiling/TeacherProfilingTeachersList'),
)
const SpecificSchoolProfilingDetails = lazy(
	() => import('./pages/assessment/TeacherProfiling/SpecificSchoolProfilingDetails'),
)
const SchoolWiseProfilingAnalytics = lazy(
	() => import('./pages/assessment/TeacherProfiling/SchoolWiseProfilingAnalytics'),
)
const SpecificStudentWellBeing = lazy(
	() => import('./pages/assessment/studentWellBeing/EditStudentWellBeing'),
)
const AddStudentChecklist = lazy(
	() => import('./pages/initiations/sendCheckList/AddStudentChecklist'),
)
const SendChecklistAnalytics = lazy(
	() => import('./pages/initiations/sendCheckList/SendChecklistAnalytics'),
)
const AddIEP = lazy(() => import('./pages/initiations/IEP/AddIEP'))
const EditIEP = lazy(() => import('./pages/initiations/IEP/EditIEP'))
const EmptyTeacherPage = lazy(() => import('./components/EmptyTeacherPage'))

function PageContentLoader({ open }) {
	const location = useLocation()
	const drawerWidth = useSelector(
		(state) => state.dashboardSliceSetup?.drawerWidth || 0,
	)
	const inAppShell = location.pathname.startsWith('/dashboard')

	if (!open) return null

	return (
		<LinearProgress
			aria-label='Loading'
			sx={{
				position: 'fixed',
				top: inAppShell ? '64px' : 0,
				left: inAppShell ? `${drawerWidth}px` : 0,
				right: 0,
				zIndex: inAppShell ? 1090 : 1400,
				height: 2,
				pointerEvents: 'none',
				transition: 'left 0.3s ease',
				backgroundColor: 'transparent',
				'& .MuiLinearProgress-bar': { backgroundColor: '#1E6BB8' },
			}}
		/>
	)
}

const App = () => {
	const dispatch = useDispatch()
	const {
		isLoading,
		showToast,
		title,
		subTitle,
		isSuccess,
		anchorOrigin,
		direction,
	} = useSelector((store) => store.toast)
	const [lightMode, setLightMode] = useState(true)
	const [typo, setTypo] = useState(englishTypography)
	const [viewToast, setViewToast] = useState(false)

	const appliedTheme = createTheme(
		lightMode ? { ...light, ...typo } : { ...dark, ...typo },
	)

	useEffect(() => {
		setViewToast(showToast)
	}, [showToast])

	useEffect(() => {
		const storedUser = getUserFromLocalStorage()
		if (!storedUser) return

		dispatch(setAppPermissions(storedUser))

		let cancelled = false
		let lastRefreshAt = 0

		const refreshLivePermissions = async ({ force = false } = {}) => {
			const now = Date.now()
			// Avoid hammering /permissions/me when user switches tabs often.
			// Tab focus only — keep rare so menus stay fresh without extra API load.
			if (!force && now - lastRefreshAt < 60_000) return
			try {
				const live = await fetchMyPermissions()
				if (cancelled || !live) return
				lastRefreshAt = Date.now()
				const latestStored = getUserFromLocalStorage() || storedUser
				const updatedUser = {
					...latestStored,
					permissions: live.permissions || latestStored.permissions,
					appFeatures: live.appFeatures || {},
				}
				addUserToLocalStorage(updatedUser)
				dispatch(setAppPermissions(updatedUser))
			} catch (_error) {
				// Keep cached permissions if refresh fails (offline / expired session).
			}
		}

		refreshLivePermissions({ force: true })

		const onVisible = () => {
			if (document.visibilityState === 'visible') {
				refreshLivePermissions()
			}
		}
		document.addEventListener('visibilitychange', onVisible)

		return () => {
			cancelled = true
			document.removeEventListener('visibilitychange', onVisible)
		}
	}, [dispatch])

	useEffect(() => {
		dispatch(resetLoginSlice())
		dispatch(clearCounsellorSlice())
		dispatch(resetClassroomsSlice())
		dispatch(resetSchoolSlice())
		dispatch(resetStudentSlice())
		if (!window.location.href.includes('/initiations/individualCase')) {
			dispatch(resetIndividualCaseSlice())
		}
		if (!window.location.href.includes('/initiations/observation')) {
			dispatch(resetObservationSlice())
		}
		if (!window.location.href.includes('/initiations/baseline')) {
			dispatch(resetBaselineSlice())
		}
		if (!window.location.href.includes('/assessment/studentWellBeing')) {
			dispatch(clearStudentWellBeingSlice())
		}
		if (!window.location.href.includes('/initiations/sendChecklist')) {
			dispatch(clearSendchecklistsliceBeingSlice())
		}
		if (!window.location.href.includes('/initiations/sendChecklist')) {
			dispatch(clearStudentIEPSlice())
		}
	}, [dispatch, window.location.href])

	const loading = ''

	return (
		<ThemeProvider theme={appliedTheme}>
			<CssBaseline />
			<BrowserRouter
				future={{
					v7_startTransition: true,
					v7_relativeSplatPath: true,
				}}
			>
				<WellbeingProvider>
				<HelpCenterProvider>
				<Routes>
					<Route
						path={routePaths.studentSelfAssessment}
						element={
							<Suspense fallback={loading}>
								<SelfAssessmentBaseline />
							</Suspense>
						}
					/>
					<Route
						path={routePaths.rmsPublicShare}
						element={
							<Suspense fallback={loading}>
								<ResourcePreview />
							</Suspense>
						}
					/>
					<Route
						path={routePaths.counsellorRegistrationPublicForm}
						element={
							<Suspense fallback={loading}>
								<PublicCounsellorRegistrationForm />
							</Suspense>
						}
					/>
					<Route
						path='/school-portal/login'
						element={<Suspense fallback={loading}><SchoolPortalLogin /></Suspense>}
					/>
					<Route
						path='/school-portal/dashboard'
						element={
							<Suspense fallback={loading}>
								<SchoolProtectedRoute>
									<SchoolPortalDashboard />
								</SchoolProtectedRoute>
							</Suspense>
						}
					/>
					<Route
						path='/school-portal/live'
						element={
							<Suspense fallback={loading}>
								<SchoolProtectedRoute>
									<VirtualSchoolPage />
								</SchoolProtectedRoute>
							</Suspense>
						}
					/>

					<Route
						path={routeConstants.root}
						element={
							<Suspense fallback={loading}>
								<ProtectedRoute>
									<ApplicationRoot />
								</ProtectedRoute>
							</Suspense>
						}
					>
						<Route
							path={routeConstants.dashboard}
							element={
								<Suspense fallback={loading}>
									<Dashboard />
								</Suspense>
							}
						>
							<Route
								path={routePaths.home}
								element={
									<Suspense fallback={loading}>
										<DashboardContent />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.counsellors}
								element={
									<Suspense fallback={loading}>
										<Counsellors />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.deactivatedUsers}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<DeactivatedUsersPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.rmsDashboard}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<ResourceDashboard />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.rmsList}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<ResourceListPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.rmsUpload}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<UploadResourcePage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.rolePermissions}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<RolePermissionManagement />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.systemConfiguration}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<SystemConfiguration />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.schoolConfiguration}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<SchoolConfiguration />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.backupLogs}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<BackupLogs />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.activityAudit}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<ActivityAuditPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.activityAuditUser}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<UserActivityReportPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.studentSupportOverview}
								element={
									<Suspense fallback={loading}>
										<StudentSupportStaffRoute superAdminOnly>
											<StudentSupportOverview />
										</StudentSupportStaffRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.studentSupportSessions}
								element={
									<Suspense fallback={loading}>
										<StudentSupportStaffRoute superAdminOnly>
											<StudentSupportSessions />
										</StudentSupportStaffRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.studentSupportSessionDetail}
								element={
									<Suspense fallback={loading}>
										<StudentSupportStaffRoute>
											<StudentSupportSessionDetail />
										</StudentSupportStaffRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.studentSupportRequests}
								element={
									<Suspense fallback={loading}>
										<StudentSupportStaffRoute>
											<StudentSupportRequests />
										</StudentSupportStaffRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.counsellorRegistrationList}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<CounsellorRegistrationList />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.websiteInquiries}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<WebsiteInquiriesPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.counsellorRegistrationDetail}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<CounsellorRegistrationDetail />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.counsellorFeedbackAdmin}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<CounsellorFeedbackAdminPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.helpCenterSosAdmin}
								element={
									<Suspense fallback={loading}>
										<SuperAdminRoute>
											<SosAdminPage />
										</SuperAdminRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.academicSchools}
								element={
									<Suspense fallback={loading}>
										<Schools />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.academicStudents}
								element={
									<Suspense fallback={loading}>
										<Students />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.academicClassrooms}
								element={
									<Suspense fallback={loading}>
										<ClassRooms />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.academicTeachers}
								element={
									<Suspense fallback={loading}>
										<Teachers />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsIndividualCase}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='IndividualCaseManagement' action='view'>
											<IndividualCase />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsObservation}
								element={
									<Suspense fallback={loading}>
										<Observation />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsObservationLog}
								element={
									<Suspense fallback={loading}>
										<ObservationV2List />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsObservationLogAdd}
								element={
									<Suspense fallback={loading}>
										<ObservationV2AddEdit />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.initiationsObservationLog}/edit/:id`}
								element={
									<Suspense fallback={loading}>
										<ObservationV2AddEdit />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsBaseline}
								element={
									<Suspense fallback={loading}>
										<Baseline />
									</Suspense>
								}
							/>

							<Route
								path={
									routePaths.initiationsSelfAssessmentDashboard
								}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='SelfAssessmentManagement' action='view'>
											<SelfAssessmentDashboard />
										</FeatureRoute>
									</Suspense>
								}
							/>

							<Route
								path={routePaths.schoolProgress}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute
											feature='BaselineManagement'
											action='view'
											anyOf={[
												'ObservationManagement',
												'IndividualCaseManagement',
												'student-IEP',
												'StudentCopeManagement',
												'studentWellBeing',
												'VirtualCounsellingManagement',
												'OneToOneCounsellingSessionManagement',
											]}
										>
											<SchoolProgressDashboard />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.inclusionProgress}
								element={
									<Suspense fallback={loading}>
										<InclusionReportPage />
									</Suspense>
								}
							/>

							<Route
								path={`${routePaths.initiationsStudentBaseline}/:row_id`}
								element={
									<Suspense fallback={loading}>
										<StudentBaseline />
									</Suspense>
								}
							/>
							<Route
								path={
									routePaths.initiationsSELCurriculumTracker
								}
								element={
									<Suspense fallback={loading}>
										<Sel />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.selTemplateAdmin}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='SELTemplateSetupManagement' action='view'>
											<SelTemplateAdmin />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsStudentCope}
								element={
									<Suspense fallback={loading}>
										<StudentCope />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.assessmentTeacherIRI}
								element={
									<Suspense fallback={loading}>
										<TeacherIRI />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.assessmentTeacherIRITeachersList}/:row_id`}
								element={
									<Suspense fallback={loading}>
										<TeacherIRITeachersList />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.assessmentTeacherIRIAssessment}
								element={
									<Suspense fallback={loading}>
										<TeacherIRIAssessment />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.schoolIRIAnalyticsReport}
								element={
									<Suspense fallback={loading}>
										<SchoolIRIAnalytics />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.specificSchoolIRIDetails}
								element={
									<Suspense fallback={loading}>
										<SpecificSchoolIRIDetails />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsstudentWellBeing}
								element={
									<Suspense fallback={loading}>
										<StudentWellBeing />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.specificStudentWellBeing}/:row_id`}
								element={
									<Suspense fallback={loading}>
										<SpecificStudentWellBeing />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.studentWellBeingAnalyticsSchools}`}
								element={
									<Suspense fallback={loading}>
										<studentWellBeingAnalyticsSchools />
									</Suspense>
								}
							/>

							<Route
								path={routePaths.newAssessmentSafeSpaces}
								element={
									<Suspense fallback={loading}>
										<SafeSpaces />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.newAssessmentSELCompetency}
								element={
									<Suspense fallback={loading}>
										<SELCompetency />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.newAssessmentPulse}
								element={
									<Suspense fallback={loading}>
										<Pulse />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.newAssessmentHeart}
								element={
									<Suspense fallback={loading}>
										<HeartEducators />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.virtualCounsellingSessions}
								element={
									<Suspense fallback={loading}>
										<Sessions />
									</Suspense>
								}
							/>
							<Route
								path={
									routePaths.virtualCounsellingSessionHistory
								}
								element={<SessionHistory />}
							/>
							{/* <Route
								path={routePaths.virtualCounsellingSchoolLogin}
								element={
									<Suspense fallback={loading}>
										<SchoolPortalLogin />
									</Suspense>
								}
							/> */}
							{/* <Route
								path={routePaths.virtualCounsellingSchoolDashboard}
								element={
									<Suspense fallback={loading}>
										<SchoolPortalDashboard />
									</Suspense>
								}
							/> */}
							<Route
								path={routePaths.virtualCounsellingManageSchools}
								element={
									<Suspense fallback={loading}>
										<ManageSchoolCredentials />
									</Suspense>
								}
							/>
							{/* <Route
								path={routePaths.virtualCounsellingSchoolPortal}
								element={
									<Suspense fallback={loading}>
										<VirtualSchoolPage />
									</Suspense>
								}
							/> */}
							<Route
								path={
									routePaths.virtualCounsellingCounsellorPortal
								}
								element={
									<Suspense fallback={loading}>
										<VirtualCounsellorPage />
									</Suspense>
								}
							/>

							<Route
								path={routePaths.virtualCounsellingSchedule}
								element={
									<Suspense fallback={loading}>
										<ScheduleMeeting />
									</Suspense>
								}
							/>

							<Route
								path={routePaths.safeSpaceSurveyHub}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceHub />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.safeSpaceSurveyCreate}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceCreateSession />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.safeSpaceSurveySessions}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceSessions />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.safeSpaceSurveyHistory}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceHistory />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.safeSpaceSurveyDetail}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceSessionDetail />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.safeSpaceSurveyReport}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceReportPage />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.safeSpaceSurveyDashboard}
								element={
									<Suspense fallback={loading}>
										<SafeSpaceDashboard />
									</Suspense>
								}
							/>

							{/* <Route
								path={routePaths.virtualCounsellingReports}
								element={
									<Suspense fallback={loading}>
										<Reports />
									</Suspense>
								}
							/> */}
							{/* 
							<Route
								path={routePaths.virtualCounsellingChat}
								element={
									<Suspense fallback={loading}>
										<ChatSupport />
									</Suspense>
								}
							/> */}

							<Route
								index
								element={
									<Navigate to={routePaths.home} replace />
								}
							/>

							<Route
								path={routePaths.TeacherProfilingAssessment}
								element={
									<Suspense fallback={loading}>
										<TeacherProfilingSchoolList />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.TeacherProfilingAssessment}/:row_id`}
								element={
									<Suspense fallback={loading}>
										<TeacherProfilingTeachersList />
									</Suspense>
								}
							/>
							<Route
								path={
									routePaths.TeacherProfilingAssessmentDetails
								}
								element={
									<Suspense fallback={loading}>
										<TeacherProfilingAssessment />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.specificSchoolProfilingDetails}
								element={
									<Suspense fallback={loading}>
										<SpecificSchoolProfilingDetails />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.schoolProfilingAnalyticsReport}
								element={
									<Suspense fallback={loading}>
										<SchoolWiseProfilingAnalytics />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsSendChecklist}
								element={
									<Suspense fallback={loading}>
										<SendChecklist />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.AddStudentChecklist}
								element={
									<Suspense fallback={loading}>
										<AddStudentChecklist />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.studentChecklistAnalytics}
								element={
									<Suspense fallback={loading}>
										<SendChecklistAnalytics />
									</Suspense>
								}
							/>

							<Route
								path={routePaths.initiationsIEP}
								element={
									<Suspense fallback={loading}>
										<StudentIEP />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsBip}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='BehaviourInterventionPlan' action='view'>
											<BipListPage />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsBipNew}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='BehaviourInterventionPlan' action='view'>
											<BipWizardPage />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.initiationsBip}/:id`}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='BehaviourInterventionPlan' action='view'>
											<BipWizardPage />
										</FeatureRoute>
									</Suspense>
								}
							/>

							{/* 🟢 MASTER IEP DASHBOARD - Traffic Police Pattern */}
							{/* This route acts as a wrapper that decides between legacy and AI IEP based on academicYear */}
							<Route
								path='initiations/student-iep/:studentId'
								element={
									<Suspense fallback={loading}>
										<IEPMasterDashboard />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.aiIep}
								element={
									<Suspense fallback={loading}>
										<StudentAIIEP />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.aiIep}/:studentId`}
								element={
									<Suspense fallback={loading}>
										<AIIEPDashboard />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsAddIEP}
								element={
									<Suspense fallback={loading}>
										<AddIEP />
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.initiationsIEP}/:row_id/edit`}
								element={
									<Suspense fallback={loading}>
										<EditIEP />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.initiationsGandT}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='GandTManagement' action='view'>
											<GandT />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={`${routePaths.gandtItapReport}/:assessmentId`}
								element={
									<Suspense fallback={loading}>
										<FeatureRoute feature='GandTManagement' action='view'>
											<ItapReportPage />
										</FeatureRoute>
									</Suspense>
								}
							/>
							<Route
								path={routePaths.gandtTemplates}
								element={
									<Suspense fallback={loading}>
										<GandTTemplateList />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.gandtTemplateAdd}
								element={
									<Suspense fallback={loading}>
										<GandTTemplateForm />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.gandtTemplateEdit}
								element={
									<Suspense fallback={loading}>
										<GandTTemplateForm />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.gandtTemplateView}
								element={
									<Suspense fallback={loading}>
										<GandTTemplateView />
									</Suspense>
								}
							/>
							<Route
								path={routePaths.gandtAssignments}
								element={
									<Suspense fallback={loading}>
										<GandTAssignmentList />
									</Suspense>
								}
							/>

							<Route
								path='*'
								element={
									<Suspense fallback={loading}>
										<PageNotFound />
									</Suspense>
								}
							/>

							<Route
								path={routePaths.teacherMsgPage}
								element={
									<Suspense fallback={loading}>
										<EmptyTeacherPage />
									</Suspense>
								}
							/>
						</Route>
					</Route>

					<Route
						path={routeConstants.auth}
						element={
							<Suspense fallback={loading}>
								<Authentication />
							</Suspense>
						}
					>
						<Route
							path={routeConstants.login}
							exact
							element={
								<Suspense fallback={loading}>
									<Login />
								</Suspense>
							}
						/>
						<Route
							path={routeConstants.accountRecovery}
							element={
								<Suspense fallback={loading}>
									<AccountRecovery />
								</Suspense>
							}
						/>
						<Route
							path={routeConstants.createNewPassword}
							element={
								<Suspense fallback={loading}>
									<CreateNewPassword />
								</Suspense>
							}
						/>
						<Route
							path={routeConstants.activateAccount}
							element={
								<Suspense fallback={loading}>
									<ActivateAccount />
								</Suspense>
							}
						/>
						<Route
							index
							element={<Navigate to={routePaths.login} replace />}
						/>
						<Route
							path='*'
							element={
								<Suspense fallback={loading}>
									<PageNotFound />
								</Suspense>
							}
						/>
					</Route>

					<Route
						index
						element={<Navigate to={routePaths.login} replace />}
					/>
					<Route
						path='*'
						element={
							<Suspense fallback={loading}>
								<PageNotFound />
							</Suspense>
						}
					/>
				</Routes>
			{viewToast && (
				<Toast
					title={title}
					subTitle={subTitle}
					isSuccess={isSuccess}
					anchorOrigin={anchorOrigin}
					direction={direction}
				/>
			)}
			<PageContentLoader open={isLoading} />
			<Suspense fallback={null}>
				<CounsellorFeedbackWidget />
			</Suspense>
			<Suspense fallback={null}>
				<HelpCenterHost />
			</Suspense>
			<Suspense fallback={null}>
				<SosHost />
			</Suspense>
			<Suspense fallback={null}>
				<MakerMark />
			</Suspense>
				</HelpCenterProvider>
				</WellbeingProvider>
			</BrowserRouter>
		</ThemeProvider>
	)
}

export default App
